import { useFetcher } from '@remix-run/react'
import { cellToParent, gridDisk } from 'h3-js'
import { useCallback, useEffect } from 'react'
import { FETCH_RESOLUTION, TWO_MINUTES_MS } from '#app/consts'
import { type PlotsActionData } from '#app/routes/resources+/plot.get-by-ids'
import { type LargeClusterActionData } from '#app/routes/resources+/plot.get-large-clusters'
import { useStoreContext } from '#app/store'

export const useFetchPlots = () => {
	const plotFetcher = useFetcher<PlotsActionData>({ key: 'plots' })
	const fetchedPlots = plotFetcher.data?.plots
	const fetchedSmallClusterPlotIds = plotFetcher.data?.smallClusterPlotIds
	const fetchedMediumClusterPlotIds = plotFetcher.data?.mediumClusterPlotIds

	const fetchedPlotIds = useStoreContext(s => s.fetchedPlotIds)

	const setFetchedPlotIds = useStoreContext(s => s.setFetchedPlotIds)
	const addLoadedPlots = useStoreContext(s => s.addLoadedPlots)
	const addSmallClusterPlotIds = useStoreContext(s => s.addSmallClusterPlotIds)
	const addMediumClusterPlotIds = useStoreContext(
		s => s.addMediumClusterPlotIds,
	)
	const setLargeClusterPlotIds = useStoreContext(s => s.setLargeClusterPlotIds)
	const setLargestClusterPlotIds = useStoreContext(
		s => s.setLargestClusterPlotIds,
	)

	const largeClusterFetcher = useFetcher<LargeClusterActionData>({
		key: 'large-cluster-plots',
	})
	const fetchedLargeClusterPlotIds =
		largeClusterFetcher.data?.largeClusterPlotIds
	const fetchedLargestClusterPlotIds =
		largeClusterFetcher.data?.largestClusterPlotIds

	const fetchPlots = useCallback(
		async (ids: string[]) => {
			const formData = new FormData()

			console.log('Fetching new plots...', ids)

			formData.append('plotIds', JSON.stringify(ids))
			plotFetcher.submit(formData, {
				method: 'POST',
				action: '/resources/plot/get-by-ids',
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	const fetchLargeClusters = useCallback(
		async () => {
			if (fetchedLargeClusterPlotIds) return

			console.log('Fetching large clusters...')
			const formData = new FormData()

			largeClusterFetcher.submit(formData, {
				method: 'POST',
				action: '/resources/plot/get-large-clusters',
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[fetchedLargeClusterPlotIds],
	)

	const reconcileVisiblePlotIds = useCallback(
		async (centerPlot: string) => {
			const parentPlotId = cellToParent(centerPlot, FETCH_RESOLUTION)
			const plotIds = gridDisk(parentPlotId, 2)

			// filter out any recently fetched Plots
			const newPlotIds = plotIds.filter(
				plotId =>
					!fetchedPlotIds.some(
						fetched =>
							fetched.plotId === plotId &&
							Date.now() - fetched.fetchedAt < TWO_MINUTES_MS,
					),
			)

			console.log('Plot ids:', plotIds)
			console.log('Reconciling visible plot ids...', newPlotIds)

			await fetchPlots(newPlotIds)
			await fetchLargeClusters()

			const newlyFetchedPlots = newPlotIds.map(plotId => ({
				plotId,
				fetchedAt: Date.now(),
			}))

			console.log('Setting fetched plot ids...', newlyFetchedPlots)

			setFetchedPlotIds(newlyFetchedPlots)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[fetchedPlotIds],
	)

	const reconcileSmallClusterPlotIds = useCallback(
		(plotIds: string[]) => {
			addSmallClusterPlotIds(plotIds)
		},
		[addSmallClusterPlotIds],
	)

	const reconcileMediumClusterPlotIds = useCallback(
		(plotIds: string[]) => {
			addMediumClusterPlotIds(plotIds)
		},
		[addMediumClusterPlotIds],
	)

	useEffect(() => {
		if (!fetchedPlots) return

		console.log('Adding fetched plots to store...', fetchedPlots)
		addLoadedPlots(fetchedPlots)
	}, [addLoadedPlots, fetchedPlots])

	useEffect(() => {
		if (!fetchedSmallClusterPlotIds) return

		reconcileSmallClusterPlotIds(fetchedSmallClusterPlotIds)
	}, [fetchedSmallClusterPlotIds, reconcileSmallClusterPlotIds])

	useEffect(() => {
		if (!fetchedMediumClusterPlotIds) return

		reconcileMediumClusterPlotIds(fetchedMediumClusterPlotIds)
	}, [fetchedMediumClusterPlotIds, reconcileMediumClusterPlotIds])

	useEffect(() => {
		if (!fetchedLargeClusterPlotIds || !fetchedLargeClusterPlotIds.length)
			return
		setLargeClusterPlotIds(fetchedLargeClusterPlotIds)
	}, [fetchedLargeClusterPlotIds, setLargeClusterPlotIds])

	useEffect(() => {
		if (!fetchedLargestClusterPlotIds || !fetchedLargestClusterPlotIds.length)
			return
		setLargestClusterPlotIds(fetchedLargestClusterPlotIds)
	}, [fetchedLargestClusterPlotIds, setLargestClusterPlotIds])

	return reconcileVisiblePlotIds
}
